import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import LocalDate from '../../../../util/LocalDate';
import { Link } from 'react-router-dom';

const KYCReviewList = ({ reviews, onReviewClick, onAssignClick, isAssigning }) => {
  const { t } = useTranslation();

  const getStatusBadge = (status) => {
    const statusMap = {
      SUBMITTED: { class: 'bg-blue', label: t('kyc.status.submitted') },
      'UNDER REVIEW': { class: 'bg-yellow', label: t('kyc.status.underReview') },
      'NEEDS MORE INFO': { class: 'bg-orange', label: t('kyc.status.needsMoreInfo') },
      APPROVED: { class: 'bg-green', label: t('kyc.status.approved') },
      REJECTED: { class: 'bg-red', label: t('kyc.status.rejected') },
      STARTED: { class: 'bg-secondary', label: t('kyc.status.started') },
    };

    const normalizedStatus = (status || '').toUpperCase().trim();
    const config = statusMap[normalizedStatus] || {
      class: 'bg-secondary',
      label: status || 'Unknown',
    };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const getTypeBadge = (type) => {
    if (type === 'W9') {
      return <span className='badge bg-blue-lt'>W-9 (US)</span>;
    } else if (type === 'W8BEN') {
      return <span className='badge bg-purple-lt'>W-8BEN (Non-US)</span>;
    }
    return <span className='badge bg-secondary-lt'>{type || 'N/A'}</span>;
  };

  return (
    <div className='table-responsive'>
      <table className='table table-vcenter card-table table-hover'>
        <thead>
          <tr>
            <th>{t('kyc.admin.table.associate')}</th>
            <th>{t('kyc.admin.table.country')}</th>
            <th>{t('kyc.admin.table.type')}</th>
            <th>{t('kyc.admin.table.submitted')}</th>
            <th>Reviewer</th>
            <th>{t('kyc.admin.table.status')}</th>
            <th className='text-end'>Actions</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((kyc) => (
            <tr key={kyc.kycId} className='cursor-pointer'>
              <td>
                <div className='d-flex align-items-center'>
                  <div>
                    <div className='fw-bold'>
                      <Link
                        to={`/customers/${kyc.associateId}/summary`}
                        className='text-reset'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {kyc.firstName && kyc.lastName
                          ? `${kyc.firstName} ${kyc.lastName}`
                          : kyc.firstName || kyc.lastName || `ID: ${kyc.associateId}`}
                      </Link>
                    </div>
                    {(kyc.firstName || kyc.lastName) && (
                      <div className='text-muted small'>ID: {kyc.associateId}</div>
                    )}
                  </div>
                </div>
              </td>
              <td>
                <span className='text-muted'>{kyc.countryCode || 'N/A'}</span>
              </td>
              <td>{getTypeBadge(kyc.type)}</td>
              <td>
                {kyc.submittedDate ? (
                  <LocalDate dateString={kyc.submittedDate} />
                ) : (
                  <span className='text-muted'>—</span>
                )}
              </td>
              <td>
                {kyc.reviewerName || kyc.reviewer ? (
                  <span className='badge bg-azure-lt'>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='14'
                      height='14'
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='2'
                      className='me-1'
                    >
                      <path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'></path>
                      <circle cx='12' cy='7' r='4'></circle>
                    </svg>
                    {kyc.reviewerName || kyc.reviewer}
                  </span>
                ) : onAssignClick ? (
                  <button
                    className='btn btn-sm btn-outline-azure'
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignClick(kyc.associateId);
                    }}
                    disabled={isAssigning === kyc.associateId}
                  >
                    {isAssigning === kyc.associateId ? (
                      <span
                        className='spinner-border spinner-border-sm'
                        role='status'
                      ></span>
                    ) : (
                      <>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='14'
                          height='14'
                          viewBox='0 0 24 24'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='2'
                          className='me-1'
                        >
                          <path d='M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'></path>
                          <circle cx='8.5' cy='7' r='4'></circle>
                          <line x1='20' y1='8' x2='20' y2='14'></line>
                          <line x1='23' y1='11' x2='17' y2='11'></line>
                        </svg>
                        Assign to Me
                      </>
                    )}
                  </button>
                ) : (
                  <span className='text-muted small'>Unassigned</span>
                )}
              </td>
              <td>{getStatusBadge(kyc.status)}</td>
              <td className='text-end'>
                <button
                  className='btn btn-sm btn-primary'
                  onClick={() => onReviewClick(kyc)}
                >
                  {t('kyc.admin.review')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

KYCReviewList.propTypes = {
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      kycId: PropTypes.number.isRequired,
      associateId: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired,
      countryCode: PropTypes.string,
      type: PropTypes.string,
      submittedDate: PropTypes.string,
      associateNotes: PropTypes.string,
      reviewer: PropTypes.string,
    })
  ).isRequired,
  onReviewClick: PropTypes.func.isRequired,
  onAssignClick: PropTypes.func,
  isAssigning: PropTypes.number,
};

KYCReviewList.defaultProps = {
  onAssignClick: null,
  isAssigning: null,
};

export default KYCReviewList;
